import * as React from "react";
import styled from "@emotion/styled";
import { useNavigate, useParams } from "react-router-dom";
import {
  AllCaps,
  Box,
  FeaturedItemGrid,
  FlexRow,
  H2,
  H3,
  Link,
  ScreenReaderText,
  Stack,
  Text,
} from "atoms";
import { canEditProfile, FeaturedGalleryItems } from "features/artistProfile";
import { useAuth } from "features/auth";
import { FollowButton } from "features/follows";
import { Feed, useFeed } from "features/posts";
import { AdminArtistProfileInfo, useArtist } from "features/reviewable";
import { ArtistReviewCard, useReviews } from "features/reviews";
import { FeaturedProducts } from "features/shop";
import { paths } from "utils/paths";
import { Head } from "features/misc";

function PageArtistProfileOverview() {
  const { user, roles } = useAuth();

  const { artistId } = useParams<{ artistId: string }>();
  const navigate = useNavigate();

  const { data: artist } = useArtist(artistId);
  const { data: updatesData } = useFeed(
    new URLSearchParams({ artistId: artistId ?? "", perPage: "4" }),
  );
  const { data: reviewsData } = useReviews({
    entityId: artist?.id,
    entityType: "ARTIST",
    perPage: 4,
  });

  const updates = React.useMemo(
    () => updatesData?.pages?.flatMap((page) => page.data) ?? [],
    [updatesData],
  );
  const reviews = React.useMemo(
    () => reviewsData?.pages?.flatMap((page) => page.data) ?? [],
    [reviewsData],
  );

  if (!artist) {
    return null;
  }
  const userCanEdit =
    roles.showAdminFeatures || (user && artist?.adminUsers?.includes(user.id));

  const editFeaturedItem = !canEditProfile({ user, roles, artist })
    ? undefined
    : (index: number) =>
        navigate(paths.artistFeaturedEdit(artist.id), { state: { index } });

  return (
    <>
      <Head title={`${artist.name} | Artist | HUG`} />
      <ScreenReaderText as="h2">Featured</ScreenReaderText>

      <Stack gap="lg">
        <FeaturedGalleryItems
          featuredItems={artist.featuredGalleryItems}
          artistId={artist.id}
          editFeaturedItem={editFeaturedItem}
          userCanEdit={userCanEdit}
        />

        {roles.showAdminFeatures && <AdminArtistProfileInfo entity={artist} />}

        {artist.shopEnabled && (
          <Stack gap="sm">
            <FlexRow>
              <SH2 size="h3">From the Shop</SH2>
              <Link
                variant="secondary"
                size="xxs"
                to={paths.artistShop(artist.id)}
              >
                Shop All
              </Link>
            </FlexRow>

            <FeaturedProducts artistId={artist.id} />
          </Stack>
        )}

        {!!updates.length && (
          <Stack gap="sm">
            <FlexRow>
              <SH2 size="h3">Recent Posts</SH2>
              <Link
                variant="secondary"
                size="xxs"
                to={paths.artistUpdates(artist.id)}
              >
                All Updates
              </Link>
              <FollowButton
                entityId={artist.id}
                size="xxs"
                variant="secondary"
              />
            </FlexRow>
            <ArtistUpdatesFeaturedFeed posts={updates} />
          </Stack>
        )}

        <ReviewsSection>
          <Stack gap="sm">
            <FlexRow>
              <SH2 size="h3">Recent Reviews</SH2>
              {!!reviews.length && (
                <Link
                  variant="secondary"
                  size="xxs"
                  to={paths.artistReviews(artist.id)}
                >
                  All Reviews
                </Link>
              )}
            </FlexRow>

            {!reviews.length ? (
              <Box>
                <Stack gap="sm">
                  <H3 textAlign="center">
                    {artist.name} doesn&apos;t have a review yet!
                  </H3>
                  <Text textAlign="center" size="xs">
                    There are no reviews posted. If you are one of the first 5
                    reviews you get double the <AllCaps>Hug</AllCaps>
                    &nbsp;Points!
                  </Text>
                </Stack>
              </Box>
            ) : (
              <FeaturedItemGrid>
                {reviews.map((review) => (
                  <ArtistReviewCard key={review.reviewId} review={review} />
                ))}
              </FeaturedItemGrid>
            )}
          </Stack>
        </ReviewsSection>
      </Stack>
    </>
  );
}

const SH2 = styled(H2)({
  margin: 0,
});

const ArtistUpdatesFeaturedFeed = styled(Feed)(({ theme }) => ({
  gap: "1rem",
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: `repeat(2, calc(50% - (${theme.spacing.gap} / 2)))`,
  },
  [theme.breakpoints.desktopLarge]: {
    gridTemplateColumns: `repeat(3, calc((100% / 3) - (${theme.spacing.gap} / 3)))`,
    "&>div:nth-of-type(4)": {
      display: "none",
    },
  },
  [theme.breakpoints.desktopXLarge]: {
    gridTemplateColumns: `repeat(4, calc(25% - (${theme.spacing.gap} * .375)))`,
    "&>div:nth-of-type(4)": {
      display: "flex",
    },
  },
}));

const ReviewsSection = styled.div({
  marginBottom: "-4rem !important",
  // marginTop: "3rem",
  // paddingTop: "3rem",
  position: "relative",
  paddingBottom: "4rem",
});

// const ReviewsSectionBG = styled(BrushWrapper)(({ theme }) => ({
//   position: "absolute",
//   left: "50%",
//   top: 0,
//   bottom: 0,
//   width: "100vw",
//   transform: "translate(-50%, 0)",
//   backgroundColor: theme.colors.fg05,
//   zIndex: 0,
//   "&+*": {
//     position: "relative",
//     zIndex: 1,
//   },
// }));
// ReviewsSectionBG.defaultProps = {
//   bg: "fg05",
//   brush: "1",
// };

export default PageArtistProfileOverview;
